import React from 'react';
import { Typography, Paper, Grid, List, ListItem, ListItemText, Chip, Divider, useTheme, useMediaQuery } from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { AspectosAMejorar as AspectosAMejorarType } from '../../../types/consolidatedReport.types';

interface AspectosAMejorarProps {
  data: AspectosAMejorarType;
}

const AspectosAMejorar: React.FC<AspectosAMejorarProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography 
        variant={isMobile ? 'body1' : 'h6'} 
        gutterBottom 
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <ErrorIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
        Aspectos a Mejorar
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Categorías Críticas */}
        {data.categorias_criticas.length > 0 && (
          <Grid item xs={12} md={4}>
            <Typography 
              variant={isMobile ? 'body2' : 'subtitle1'} 
              sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}
            >
              Categorías Críticas (&lt;60%)
            </Typography>
            <List dense={isMobile}>
              {data.categorias_criticas.map((cat, index) => (
                <React.Fragment key={cat.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 14 }}>{cat.name}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ fontSize: isMobile ? 10 : 12 }}>
                          {cat.average.toFixed(1)}% - {cat.audit_count} auditorías
                        </Typography>
                      }
                    />
                    <Chip 
                      label={`${cat.average.toFixed(0)}%`} 
                      color="error" 
                      size="small" 
                      sx={{ fontSize: isMobile ? 9 : 11 }}
                    />
                  </ListItem>
                  {index < data.categorias_criticas.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}

        {/* Categorías en Riesgo */}
        {data.categorias_riesgo.length > 0 && (
          <Grid item xs={12} md={4}>
            <Typography 
              variant={isMobile ? 'body2' : 'subtitle1'} 
              sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}
            >
              Categorías en Riesgo (60-79%)
            </Typography>
            <List dense={isMobile}>
              {data.categorias_riesgo.map((cat, index) => (
                <React.Fragment key={cat.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 14 }}>{cat.name}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ fontSize: isMobile ? 10 : 12 }}>
                          {cat.average.toFixed(1)}%
                        </Typography>
                      }
                    />
                    <Chip 
                      label={`${cat.average.toFixed(0)}%`} 
                      color="warning" 
                      size="small"
                      sx={{ fontSize: isMobile ? 9 : 11 }}
                    />
                  </ListItem>
                  {index < data.categorias_riesgo.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}

        {/* Top 5 Preguntas Peores */}
        {data.top_preguntas_peores.length > 0 && (
          <Grid item xs={12} md={4}>
            <Typography 
              variant={isMobile ? 'body2' : 'subtitle1'} 
              sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}
            >
              Preguntas con Mayor Incidencia
            </Typography>
            <List dense={isMobile}>
              {data.top_preguntas_peores.map((pregunta, index) => (
                <React.Fragment key={pregunta.id}>
                  <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: isMobile ? 11 : 13 }}>
                          {pregunta.text}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: isMobile ? 9 : 11 }}>
                            {pregunta.category}
                          </Typography>
                          <Typography variant="caption" color="error.main" sx={{ fontSize: isMobile ? 9 : 11 }}>
                            {pregunta.average.toFixed(1)}% • {pregunta.recurrence} veces
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < data.top_preguntas_peores.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}
      </Grid>

      {data.categorias_criticas.length === 0 && data.categorias_riesgo.length === 0 && data.top_preguntas_peores.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          ¡Excelente! No hay aspectos críticos identificados en este periodo.
        </Typography>
      )}
    </Paper>
  );
};

export default AspectosAMejorar;
